import {
  InteractionRequiredAuthError,
  PopupRequest,
  SilentRequest,
} from "@azure/msal-browser";

import { loginRequest } from "../authConfig";
import { msalInstance } from "../index";
import { setAccessTokenCookie } from "./cookies";
import { getEnv } from "./env";
import { authDebug, authDebugError } from "./safeAuthLog";
import { logoutUser } from "./tokenManager";

export type ApiError = Error & { status?: number; detail?: string };

// Extract FastAPI's `detail` field from a non-OK response so callers can show
// the backend's actual error message instead of a generic "request failed".
async function buildApiError(response: Response): Promise<ApiError> {
  let detail: string | undefined;
  try {
    const body = await response.clone().json();
    const raw = body?.detail;
    if (typeof raw === "string") {
      detail = raw;
    } else if (raw != null) {
      detail = JSON.stringify(raw);
    }
  } catch {
    // Body wasn't JSON (HTML error page, empty body, etc.) — leave detail undefined.
  }

  const message = detail
    ? `API call failed with status ${response.status}: ${detail}`
    : `API call failed with status ${response.status}: ${response.statusText}`;
  const error = new Error(message) as ApiError;
  error.status = response.status;
  error.detail = detail;
  return error;
}

/**
 * Simplified API call function that always gets fresh tokens from MSAL
 * This eliminates token synchronization issues when multiple users share an account
 */
export async function callApi(
  apiUri: string,
  method: string,
  queryParams: Record<string, string> | null = null,
  bodyParams: FormData | Record<string, any> | null = null
) {
  // Normalise the base URL: tolerate VITE_BACKEND_URL with or without a
  // trailing slash. Front Door origins are configured without one (e.g.
  // https://…azurefd.net), which otherwise concatenates to "…netapi/v1/…".
  const baseAPIUrl = getEnv("VITE_BACKEND_URL").replace(/\/+$/, "");
  const apiUrl = `${baseAPIUrl}/api/v1/${apiUri}`;

  // Always get a fresh token from MSAL - let MSAL handle refresh tokens
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error("No active account, user needs to sign in");
  }

  const tokenRequest: SilentRequest = {
    ...loginRequest,
    account,
    scopes: loginRequest.scopes.filter(
      (scope): scope is string => scope !== undefined
    ),
  };

  // Let MSAL deal with using refresh tokens etc.
  let accessToken: string;
  try {
    authDebug("callApi: acquiring token silently");
    const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
    accessToken = tokenResponse.accessToken;
  } catch (error) {
    authDebugError("callApi: silent token acquisition failed, trying popup", error);
    if (error instanceof InteractionRequiredAuthError) {
      const popupResponse = await msalInstance.acquireTokenPopup(
        tokenRequest as PopupRequest
      );
      accessToken = popupResponse.accessToken;
    } else {
      throw new Error("Failed to acquire access token");
    }
  }

  // Append query parameters to the API URI
  const queryString = new URLSearchParams(queryParams || {}).toString();
  const apiUriWithQuery = queryString ? `${apiUrl}?${queryString}` : apiUrl;

  // Set up headers
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${accessToken}`);

  // Check if bodyParams is a FormData instance
  let requestBody: FormData | string | null = null;
  if (bodyParams) {
    if (bodyParams instanceof FormData) {
      // Do not set 'Content-Type' manually; browser will handle it.
      requestBody = bodyParams;
    } else {
      headers.append("Content-Type", "application/json");
      requestBody = JSON.stringify(bodyParams);
    }
  }

  // Configure fetch options
  const options = {
    method: method,
    headers: headers,
    body: requestBody,
  };

  const response = await fetch(apiUriWithQuery, options);

  // Handle 401 Unauthorized - retry with a fresh token
  if (response.status === 401) {
    authDebug("callApi: received 401, attempting token refresh");

    try {
      // Try to get a fresh token silently first (MSAL will use refresh token if needed)
      let freshToken: string;
      try {
        const retryTokenResponse =
          await msalInstance.acquireTokenSilent(tokenRequest);
        freshToken = retryTokenResponse.accessToken;
      } catch (silentError) {
        // If silent refresh fails, try popup (user interaction)
        if (silentError instanceof InteractionRequiredAuthError) {
          authDebug("callApi: silent refresh failed, trying popup");
          const popupResponse = await msalInstance.acquireTokenPopup(
            tokenRequest as PopupRequest
          );
          freshToken = popupResponse.accessToken;
          setAccessTokenCookie(freshToken);
        } else {
          throw silentError;
        }
      }

      // Retry the request with the fresh token
      headers.set("Authorization", `Bearer ${freshToken}`);
      const retryResponse = await fetch(apiUriWithQuery, {
        ...options,
        headers,
      });

      if (retryResponse.status === 401) {
        authDebug("callApi: still 401 after refresh; logging out");
        await logoutUser(true);
        throw new Error("Authentication failed. Please sign in again.");
      }

      if (!retryResponse.ok) {
        throw await buildApiError(retryResponse);
      }

      const responseData = await retryResponse.json();
      return responseData;
    } catch (retryError) {
      authDebugError(
        "callApi: error refreshing token or retrying request",
        retryError
      );
      // Only logout if we couldn't refresh the token at all
      // If it's an InteractionRequiredAuthError that we couldn't handle, logout
      if (retryError instanceof InteractionRequiredAuthError) {
        authDebug("callApi: refresh requires interaction; logging out");
        await logoutUser(true);
        throw new Error("Authentication failed. Please sign in again.");
      }
      // If it's an error from the retry request itself (not token refresh), re-throw it
      throw retryError;
    }
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }

  const responseData = await response.json();
  return responseData;
}
